import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calculator, 
  KanbanSquare, 
  ListChecks, 
  Settings,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  LogOut,
  MessageSquare,
  Users,
  Compass,
  Megaphone,
  Zap,
  FileText,
  CalendarDays,
  Briefcase,
  Receipt,
  BarChart2,
  TrendingUp,
  Truck,
  ShieldAlert,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { cn } from '../lib/utils';
import cleanbidLogo from '../assets/cleanbid-logo.png';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompany } from '../context/CompanyContext';

/**
 * Sidebar navigation with grouped sections:
 * 
 * Dashboard
 * — Revenue & Quoting —
 * Quotes, Calculator
 * — CRM —
 * Pipeline, Leads, Clients
 * — Operations —
 * Tasks, Calendar, Inbox
 * — Automation & Growth —
 * Playbooks, Engage, Business Coach
 * Settings
 * Logout
 */

// Navigation groups with dividers
const navigationGroups = [
  {
    id: 'top',
    label: null,
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['owner', 'admin', 'sales', 'technician', 'marketing'] },
    ]
  },
  {
    id: 'revenue',
    label: 'Revenue & Quoting',
    items: [
      { name: 'Quotes', href: '/quotes', icon: FileText, hint: 'Build proposals', roles: ['owner', 'admin', 'sales'] },
      { name: 'Calculator', href: '/calculator', icon: Calculator, hint: 'Measured / sq ft pricing', roles: ['owner', 'admin', 'sales'] },
    ]
  },
  {
    id: 'crm',
    label: 'CRM',
    items: [
      { name: 'Pipeline', href: '/pipeline', icon: KanbanSquare, roles: ['owner', 'admin', 'sales'] },
      { name: 'Leads', href: '/leads', icon: ListChecks, roles: ['owner', 'admin', 'sales', 'marketing'] },
      { name: 'Clients', href: '/clients', icon: Users, roles: ['owner', 'admin', 'sales', 'technician'] },
    ]
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { name: 'Jobs',      href: '/jobs',      icon: Briefcase,    roles: ['owner', 'admin', 'sales', 'technician', 'marketing'] },
      { name: 'Dispatch',  href: '/dispatch',  icon: Truck,        hint: 'Run today',          roles: ['owner', 'admin', 'sales', 'technician'] },
      { name: 'Calendar',  href: '/calendar',  icon: CalendarDays, hint: 'Plan your week',     roles: ['owner', 'admin', 'sales', 'technician', 'marketing'] },
      { name: 'Forecast',  href: '/forecast',  icon: TrendingUp,   hint: 'See what\'s coming', roles: ['owner', 'admin', 'sales'] },
      { name: 'Tasks',     href: '/tasks',     icon: CheckSquare,  roles: ['owner', 'admin', 'sales', 'technician', 'marketing'] },
      { name: 'Invoices',  href: '/invoices',  icon: Receipt,      roles: ['owner', 'admin', 'sales'] },
      { name: 'Inbox',     href: '/inbox',     icon: MessageSquare, roles: ['owner', 'admin', 'sales'] },
    ]
  },
  {
    id: 'automation',
    label: 'Automation & Growth',
    items: [
      { name: 'Playbooks', href: '/playbooks', icon: Zap, roles: ['owner', 'admin', 'sales'] },
      { name: 'Engage', href: '/engage', icon: Megaphone, roles: ['owner', 'admin', 'sales', 'marketing'] },
      { name: 'Business Coach', href: '/coach', icon: Compass, roles: ['owner', 'admin'] },
    ]
  },
  {
    id: 'analytics',
    label: 'Analytics',
    items: [
      { name: 'Reports', href: '/reports', icon: BarChart2, roles: ['owner', 'admin', 'sales'] },
    ]
  },
  {
    id: 'admin',
    label: null,
    items: [
      { name: 'Settings', href: '/settings', icon: Settings, roles: ['owner', 'admin'] },
    ]
  },
];

const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, profile } = useAuth();
  const { companyId } = useCompany();
  const { role, loading: roleLoading, isRoleHydrating } = useRole();
  const effectiveRoleLoading = roleLoading || isRoleHydrating;
  const [totalUnread, setTotalUnread] = useState(0);
  const [unpaidInvoiceCount, setUnpaidInvoiceCount] = useState(0);

  const isPlatformAdmin = profile?.isPlatformAdmin === true;

  // Filter navigation groups based on user role
  const filteredGroups = useMemo(() => {
    if (effectiveRoleLoading || role === null) {
      return [];
    }
    
    return navigationGroups
      .map(group => ({
        ...group,
        items: group.items.filter(item => item.roles.includes(role))
      }))
      .filter(group => group.items.length > 0);
  }, [role, effectiveRoleLoading]);

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Listen for unread count changes
  useEffect(() => {
    if (!db || !companyId) return;

    const unsubscribe = onSnapshot(
      collection(db, "companies", companyId, "conversations"),
      (snapshot) => {
        const unread = snapshot.docs.reduce((sum, doc) => {
          const data = doc.data();
          return sum + (data.unreadCount || 0);
        }, 0);
        setTotalUnread(unread);
      },
      (error) => {
        console.error("Error listening to conversations:", error);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  // Listen for unpaid invoice count (for sidebar badge)
  useEffect(() => {
    if (!db || !companyId) return;

    const unsubscribe = onSnapshot(
      collection(db, "companies", companyId, "invoices"),
      (snapshot) => {
        const unpaid = snapshot.docs.filter(doc => doc.data().status !== 'paid').length;
        setUnpaidInvoiceCount(unpaid);
      },
      (error) => {
        console.error("Error listening to invoices:", error);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    const isInbox = item.name === 'Inbox';
    const isInvoices = item.name === 'Invoices';
    const showInboxBadge = isInbox && totalUnread > 0;
    const showInvoiceBadge = isInvoices && unpaidInvoiceCount > 0;
    const showBadge = showInboxBadge || showInvoiceBadge;
    const badgeCount = isInbox ? totalUnread : unpaidInvoiceCount;
    const badgeColor = isInbox ? 'bg-blue-600' : 'bg-amber-500';

    return (
      <Link
        key={item.name}
        to={item.href}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 relative',
          collapsed ? 'justify-center' : '',
          active
            ? 'bg-[rgba(242,107,29,0.15)] text-[var(--cb-orange)] font-medium shadow-sm'
            : 'text-[var(--cb-dark)] hover:bg-gray-100 hover:text-[var(--cb-orange)]'
        )}
        title={collapsed ? item.name : ''}
      >
        <Icon className={cn('h-5 w-5 flex-shrink-0')} />
        {!collapsed && (
          <span className="flex-1 min-w-0">
            <span className="text-sm flex items-center gap-2">
              {item.name}
              {showBadge && (
                <span className={`px-2 py-0.5 text-xs ${badgeColor} text-white rounded-full`} data-testid={`sidebar-badge-${item.name.toLowerCase()}`}>
                  {badgeCount}
                </span>
              )}
            </span>
            {item.hint && (
              <span className="text-[10px] text-gray-400 leading-tight block mt-0.5">{item.hint}</span>
            )}
          </span>
        )}
        {collapsed && showBadge && (
          <span className={`absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] ${badgeColor} text-white rounded-full`} data-testid={`sidebar-badge-${item.name.toLowerCase()}-collapsed`}>
            {badgeCount}
          </span>
        )}
      </Link>
    );
  };

  return (
    <aside
      className={cn(
        'bg-white border-r border-gray-200 flex flex-col h-screen transition-all duration-300 ease-in-out',
        collapsed ? 'w-[72px]' : 'w-[240px]'
      )}
    >
      {/* Sidebar Header — CleanBid.ai brand lockup */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-center gap-2">
        <img
          src={cleanbidLogo}
          alt="CleanBid.ai"
          className={cn(
            'transition-all object-contain',
            collapsed ? 'w-10' : 'w-11'
          )}
        />
        {!collapsed && (
          <span style={{ fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.01em', color: '#10151D' }}>
            CleanBid<span style={{ color: '#116F73' }}>.ai</span>
          </span>
        )}
      </div>

      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="p-3 hover:bg-gray-100 border-b border-gray-200 flex items-center justify-center transition-colors"
      >
        {collapsed ? (
          <ChevronRight className="h-5 w-5 text-gray-600" />
        ) : (
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        )}
      </button>

      {/* Navigation Items - Scrollable */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
        {effectiveRoleLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            {!collapsed && <p className="text-sm text-gray-500 mt-2">Loading menu...</p>}
          </div>
        ) : role === null ? (
          <div className="text-center text-sm text-gray-500 py-4">
            <p>No role assigned</p>
            {!collapsed && <p className="text-xs mt-1">Contact admin</p>}
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-4">
            No menu items available
          </div>
        ) : (
          <>
            {filteredGroups.map((group, groupIndex) => (
              <div key={group.id}>
                {/* Group divider with label */}
                {group.label && (
                  <div className={cn('pt-3 pb-2', groupIndex > 0 && 'mt-2 border-t border-gray-200')}>
                    {!collapsed && (
                      <span className="px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                        {group.label}
                      </span>
                    )}
                    {collapsed && (
                      <div className="flex justify-center">
                        <div className="w-6 border-t border-gray-300" />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Divider for unlabeled groups (Settings) */}
                {!group.label && groupIndex > 0 && (
                  <div className="my-3 border-t border-gray-200" />
                )}

                {/* Group items */}
                <div className="space-y-1">
                  {group.items.map(renderNavItem)}
                </div>
              </div>
            ))}
          </>
        )}
      </nav>

      {/* Logout Button - Fixed at Bottom */}
      <div className="flex-shrink-0 p-3 border-t border-gray-200">
        {/* Platform Admin link — only for KRO internal staff */}
        {isPlatformAdmin && (
          <Link
            to="/admin"
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 mb-1',
              collapsed ? 'justify-center' : '',
              isActive('/admin')
                ? 'bg-indigo-50 text-indigo-700 font-medium'
                : 'text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700'
            )}
            title={collapsed ? 'Platform Admin' : ''}
            data-testid="sidebar-platform-admin"
          >
            <ShieldAlert className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span className="text-xs font-semibold">Platform Admin</span>}
          </Link>
        )}
        <button
          onClick={handleLogout}
          className={cn(
            'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg transition-all duration-200',
            'text-red-500 hover:bg-red-50 hover:text-red-600',
            collapsed ? 'justify-center' : ''
          )}
          title={collapsed ? 'Logout' : ''}
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
